import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { Link } from 'react-router-dom';
import { useTranslation } from '@refinedev/core';
import {
  extractManitobaClaimQueryResponseItems,
  type ClaimQueryRecord,
  type ManitobaClaimQueryResponseItem,
} from '@billing/validation/claimQuery';
import {
  IconBuildingHospital,
  IconFileDescription,
  IconMessageCircle,
  IconRefresh,
} from '@tabler/icons-react';
import { DateTime } from 'luxon';
import {
  formatClaimQueryPersonName,
  renderClaimQueryStatusBadge,
} from 'columns/ClaimQuery';
import { useIsMobile } from 'hooks/useIsMobile';
import { formatDateTime } from 'utils/dateUtils';
import { isRecord } from 'utils/objectUtils';
import { CLAIMS_WINDOW_TARGET } from 'utils/crossTabNavigation';

type ClaimQueryDetailsModalProps = {
  opened: boolean;
  onClose: () => void;
  claimQuery: ClaimQueryRecord | null;
  isLoading: boolean;
  canRefresh: boolean;
  canViewClaims: boolean;
  isRefreshing: boolean;
  onRefresh: (queryID: string) => Promise<void>;
  timezone?: string | null;
};

type SummaryFieldProps = {
  label: string;
  value: string;
};

const EMPTY_VALUE = '—';

const getStringField = (value: unknown, field: string): string => {
  if (!isRecord(value)) return '';
  const fieldValue = value[field];
  return typeof fieldValue === 'string' ? fieldValue : '';
};

const firstNonEmpty = (...values: Array<string | null | undefined>): string =>
  values.find(Boolean) ?? '';

const formatCompactDate = (
  value: string | null | undefined,
  timezone?: string | null
): string => {
  if (!value) return EMPTY_VALUE;

  const parsed = DateTime.fromFormat(value, 'yyyyMMdd', {
    zone: timezone || undefined,
  });
  return parsed.isValid ? parsed.toLocaleString(DateTime.DATE_MED) : value;
};

const formatCurrency = (value: string | null | undefined): string => {
  if (!value) return EMPTY_VALUE;

  const amount = Number(value);
  if (!Number.isFinite(amount)) return value;

  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
  }).format(amount);
};

const SummaryField = ({ label, value }: SummaryFieldProps) => (
  <Stack gap={2}>
    <Text size="xs" c="dimmed" fw={600} tt="uppercase">
      {label}
    </Text>
    <Text size="sm" fw={500} style={{ wordBreak: 'break-word' }}>
      {value || EMPTY_VALUE}
    </Text>
  </Stack>
);

const getResponseStatus = (
  item: ManitobaClaimQueryResponseItem,
  labels: {
    completed: string;
    pending: string;
    noQueryFound: string;
    unknownStatus: string;
  }
) => {
  const status = item.querySearchItem.status;

  if (status === '90' || status === '91') {
    return { color: 'green', label: labels.completed };
  }
  if (status === '92') {
    return { color: 'yellow', label: labels.pending };
  }
  if (!status) {
    return { color: 'gray', label: labels.noQueryFound };
  }

  return { color: 'gray', label: labels.unknownStatus };
};

const getEobCodes = (item: ManitobaClaimQueryResponseItem): string[] => {
  const codes = [
    ...(item.eobCode?.split(',') ?? []),
    ...(item.eobDescription?.map((description) => description.eobCode) ?? []),
  ]
    .map((code) => code.trim())
    .filter(Boolean);

  return Array.from(new Set(codes));
};

export const ClaimQueryDetailsModal = ({
  opened,
  onClose,
  claimQuery,
  isLoading,
  canRefresh,
  canViewClaims,
  isRefreshing,
  onRefresh,
  timezone,
}: ClaimQueryDetailsModalProps) => {
  const { translate } = useTranslation();
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('dark');
  const isMobile = useIsMobile(768);
  const responseItems = extractManitobaClaimQueryResponseItems(
    claimQuery?.rawResponse
  );
  const firstResponseItem = responseItems[0];
  const creatorName =
    formatClaimQueryPersonName(claimQuery?.createdByUser) ||
    claimQuery?.createdByUser?.email ||
    EMPTY_VALUE;
  const claimNumber = claimQuery?.claim?.claimNumber || EMPTY_VALUE;
  const patientName = responseItems
    .map((item) => [item.givenName, item.surName].filter(Boolean).join(' '))
    .find(Boolean);
  const phin = firstNonEmpty(
    getStringField(claimQuery?.statusRequestPayload, 'phinId'),
    getStringField(claimQuery?.submittedPayload, 'phinId')
  );
  const microfilm = firstNonEmpty(
    firstResponseItem?.microFilmNumber,
    getStringField(claimQuery?.statusRequestPayload, 'microFilmNumber'),
    getStringField(claimQuery?.submittedPayload, 'microFilmNumber')
  );
  const externalClaimID = firstNonEmpty(
    firstResponseItem?.externalClaimId,
    getStringField(claimQuery?.statusRequestPayload, 'claimId'),
    getStringField(claimQuery?.submittedPayload, 'claimId')
  );
  const originalMessage = firstNonEmpty(
    getStringField(claimQuery?.submittedPayload, 'remarks'),
    getStringField(claimQuery?.submittedPayload, 'comment')
  );
  const labels = {
    completed: translate(
      'pages:queries.details.manitobaStatus.completed',
      'Completed'
    ),
    pending: translate(
      'pages:queries.details.manitobaStatus.pending',
      'Pending'
    ),
    noQueryFound: translate(
      'pages:queries.details.manitobaStatus.noQueryFound',
      'No query found'
    ),
    unknownStatus: translate(
      'pages:queries.details.manitobaStatus.unknownStatus',
      'Unknown status'
    ),
  };
  const isAwaitingResponse =
    claimQuery?.status === 'CREATED' || claimQuery?.status === 'SUBMITTED';
  const emptyResponseTitle = isAwaitingResponse
    ? translate(
        'pages:queries.details.waitingForResponse',
        'Waiting for Manitoba response'
      )
    : translate(
        'pages:queries.details.responseUnavailable',
        'Response details unavailable'
      );
  const emptyResponseDescription = isAwaitingResponse
    ? translate(
        'pages:queries.details.waitingForResponseDescription',
        'Tariff response details will appear after Manitoba processes the query.'
      )
    : claimQuery?.status === 'FAILED'
    ? translate(
        'pages:queries.details.failedResponseDescription',
        'This query was unsuccessful, so no tariff-level response is available.'
      )
    : translate(
        'pages:queries.details.missingResponseDescription',
        'Manitoba did not return tariff-level details for this query.'
      );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      fullScreen={isMobile}
      centered={!isMobile}
      radius="lg"
      padding="lg"
      overlayProps={{ blur: 3, opacity: 0.5 }}
      data-testid="claim-query-details-modal"
      closeButtonProps={{
        'aria-label': translate(
          'pages:queries.details.close',
          'Close query details'
        ),
      }}
      title={
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon variant="light" color="blue" radius="md" size="lg">
            <IconFileDescription size={19} />
          </ThemeIcon>
          <Box>
            <Title order={3} lh={1.15}>
              {translate('pages:queries.details.title', 'Claim Query Details')}
            </Title>
            <Text size="xs" c="dimmed">
              {translate(
                'pages:queries.details.subtitle',
                'Review the submitted query and Manitoba response.'
              )}
            </Text>
          </Box>
        </Group>
      }
      styles={{
        content: {
          background:
            colorScheme === 'dark'
              ? theme.colors.dark[7]
              : theme.colors.gray[0],
          border: `1px solid ${
            colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]
          }`,
        },
      }}
    >
      {!claimQuery && isLoading ? (
        <Flex justify="center" align="center" mih={320}>
          <Loader data-testid="query-detail-loader" />
        </Flex>
      ) : claimQuery ? (
        <ScrollArea.Autosize
          mah={isMobile ? 'calc(100dvh - 96px)' : '75vh'}
          type="auto"
          offsetScrollbars
        >
          <Stack gap="lg" pr="xs">
            <Paper withBorder radius="md" p="md" shadow="xs">
              <Group justify="space-between" align="flex-start" wrap="wrap">
                <Group gap="md" wrap="nowrap">
                  <ThemeIcon
                    variant="light"
                    color="indigo"
                    radius="xl"
                    size={48}
                  >
                    <IconBuildingHospital size={23} />
                  </ThemeIcon>
                  <Box>
                    {canViewClaims && claimQuery.claim?.claimNumber ? (
                      <Anchor
                        component={Link}
                        to={`/claims/edit/${encodeURIComponent(
                          claimQuery.claim.id
                        )}`}
                        target={CLAIMS_WINDOW_TARGET}
                        fw={700}
                        size="lg"
                        aria-label={`${translate(
                          'pages:queries.table.openClaim',
                          'Open claim'
                        )} ${claimNumber}`}
                      >
                        {claimNumber}
                      </Anchor>
                    ) : (
                      <Text fw={700} size="lg">
                        {claimNumber}
                      </Text>
                    )}
                    <Text size="sm" c="dimmed">
                      {claimQuery.site?.name ||
                        translate(
                          'pages:queries.details.unknownSite',
                          'Unknown site'
                        )}
                    </Text>
                  </Box>
                </Group>
                <Group gap="sm">
                  {renderClaimQueryStatusBadge(claimQuery.status)}
                  {canRefresh ? (
                    <Button
                      variant="light"
                      leftSection={<IconRefresh size={16} />}
                      loading={isRefreshing}
                      onClick={() => void onRefresh(claimQuery.id)}
                    >
                      {translate(
                        'pages:queries.details.refreshStatus',
                        'Refresh Status'
                      )}
                    </Button>
                  ) : null}
                </Group>
              </Group>
            </Paper>

            <Stack gap="sm">
              <Title order={4}>
                {translate('pages:queries.details.summary', 'Query Summary')}
              </Title>
              <Paper withBorder radius="md" p="md">
                <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="md">
                  <SummaryField
                    label={translate(
                      'pages:queries.details.fields.queryType',
                      'Query type'
                    )}
                    value={claimQuery.queryType}
                  />
                  <SummaryField
                    label={translate(
                      'pages:queries.details.fields.patient',
                      'Patient'
                    )}
                    value={patientName || EMPTY_VALUE}
                  />
                  <SummaryField
                    label={translate(
                      'pages:queries.details.fields.phin',
                      'PHIN'
                    )}
                    value={phin || EMPTY_VALUE}
                  />
                  <SummaryField
                    label={translate(
                      'pages:queries.details.fields.createdBy',
                      'Created by'
                    )}
                    value={creatorName}
                  />
                  <SummaryField
                    label={translate(
                      'pages:queries.details.fields.microfilm',
                      'Microfilm number'
                    )}
                    value={microfilm || EMPTY_VALUE}
                  />
                  <SummaryField
                    label={translate(
                      'pages:queries.details.fields.externalClaimId',
                      'External claim ID'
                    )}
                    value={externalClaimID || EMPTY_VALUE}
                  />
                  <SummaryField
                    label={translate(
                      'pages:queries.details.fields.created',
                      'Created'
                    )}
                    value={formatDateTime(claimQuery.createdAt, {
                      timezone,
                      fallback: EMPTY_VALUE,
                    })}
                  />
                  <SummaryField
                    label={translate(
                      'pages:queries.details.fields.lastPolled',
                      'Last polled'
                    )}
                    value={formatDateTime(claimQuery.lastPolledAt, {
                      timezone,
                      fallback: EMPTY_VALUE,
                    })}
                  />
                </SimpleGrid>
              </Paper>
            </Stack>

            <Stack gap="sm">
              <Title order={4}>
                {translate(
                  'pages:queries.details.originalMessage',
                  'Original Message'
                )}
              </Title>
              <Paper withBorder radius="md" p="md">
                <Text size="sm">
                  {originalMessage ||
                    translate(
                      'pages:queries.details.noOriginalMessage',
                      'No submitted message was recorded.'
                    )}
                </Text>
              </Paper>
            </Stack>

            <Stack gap="sm">
              <Box>
                <Title order={4}>
                  {translate(
                    'pages:queries.details.manitobaResponse',
                    'Manitoba Response'
                  )}
                </Title>
                <Text size="sm" c="dimmed">
                  {translate(
                    'pages:queries.details.responseDescription',
                    'Status and resolution details returned for each tariff.'
                  )}
                </Text>
              </Box>

              {responseItems.length ? (
                <Stack gap="md" data-testid="query-detail-response-items">
                  {responseItems.map((item, index) => {
                    const status = getResponseStatus(item, labels);
                    const eobCodes = getEobCodes(item);
                    const itemKey = `${item.itemNo || 'item'}-${
                      item.serviceCode || index
                    }`;

                    return (
                      <Paper
                        key={itemKey}
                        withBorder
                        radius="md"
                        p="md"
                        data-testid={`query-detail-response-item-${index}`}
                      >
                        <Stack gap="md">
                          <Group
                            justify="space-between"
                            align="flex-start"
                            wrap="wrap"
                          >
                            <Box>
                              <Text
                                fw={700}
                                data-testid={`query-detail-item-title-${index}`}
                              >
                                {translate(
                                  'pages:queries.details.item',
                                  'Item'
                                )}{' '}
                                {item.itemNo || EMPTY_VALUE}
                              </Text>
                              <Text size="sm" c="dimmed">
                                {translate(
                                  'pages:queries.details.serviceCode',
                                  'Service code'
                                )}{' '}
                                {item.serviceCode || EMPTY_VALUE}
                              </Text>
                            </Box>
                            <Badge
                              color={status.color}
                              variant="light"
                              data-testid={`query-detail-response-status-${index}`}
                            >
                              {status.label}
                            </Badge>
                          </Group>

                          <Alert
                            color={status.color}
                            variant="light"
                            radius="md"
                            icon={<IconMessageCircle size={20} />}
                            title={translate(
                              'pages:queries.details.responseMessage',
                              'Response from Manitoba'
                            )}
                            data-testid={`query-detail-response-message-${index}`}
                          >
                            <Text size="sm" fw={600}>
                              {item.querySearchItem.remarks ||
                                translate(
                                  'pages:queries.details.noRemarks',
                                  'No Manitoba remarks were returned.'
                                )}
                            </Text>
                          </Alert>

                          <SimpleGrid
                            cols={{ base: 2, sm: 3, md: 4 }}
                            spacing="md"
                          >
                            <SummaryField
                              label={translate(
                                'pages:queries.details.fields.serviceDate',
                                'Service date'
                              )}
                              value={formatCompactDate(
                                item.serviceDate,
                                timezone
                              )}
                            />
                            <SummaryField
                              label={translate(
                                'pages:queries.details.fields.processedDate',
                                'Processed date'
                              )}
                              value={formatCompactDate(
                                item.processedDate,
                                timezone
                              )}
                            />
                            <SummaryField
                              label={translate(
                                'pages:queries.details.fields.billedAmount',
                                'Billed amount'
                              )}
                              value={formatCurrency(item.billAmt)}
                            />
                            <SummaryField
                              label={translate(
                                'pages:queries.details.fields.paidAmount',
                                'Paid amount'
                              )}
                              value={formatCurrency(item.paymentAmount)}
                            />
                          </SimpleGrid>

                          {eobCodes.length || item.eobDescription?.length ? (
                            <Stack gap="sm">
                              <Divider />
                              <Group gap="xs">
                                <Text size="sm" fw={600}>
                                  {translate(
                                    'pages:queries.details.eobCodes',
                                    'EOB codes'
                                  )}
                                </Text>
                                {eobCodes.map((code) => (
                                  <Badge
                                    key={code}
                                    color="gray"
                                    variant="outline"
                                    size="sm"
                                  >
                                    {code}
                                  </Badge>
                                ))}
                              </Group>
                              {item.eobDescription?.map(
                                (description, descriptionIndex) => (
                                  <Group
                                    key={`${description.eobCode}-${descriptionIndex}`}
                                    gap="sm"
                                    align="flex-start"
                                    wrap="nowrap"
                                  >
                                    <Badge color="red" variant="light" mt={2}>
                                      {description.eobCode}
                                    </Badge>
                                    <Text size="sm" style={{ flex: 1 }}>
                                      {description.eobCodeDesc}
                                    </Text>
                                  </Group>
                                )
                              )}
                            </Stack>
                          ) : null}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              ) : (
                <Paper
                  withBorder
                  radius="md"
                  p="xl"
                  ta="center"
                  data-testid="query-detail-empty-response"
                >
                  <Text fw={600}>{emptyResponseTitle}</Text>
                  <Text size="sm" c="dimmed">
                    {emptyResponseDescription}
                  </Text>
                </Paper>
              )}
            </Stack>
          </Stack>
        </ScrollArea.Autosize>
      ) : (
        <Flex justify="center" align="center" mih={240}>
          <Alert
            color="red"
            variant="light"
            title={translate(
              'pages:queries.details.loadError',
              'Unable to load query details'
            )}
          >
            {translate(
              'pages:queries.details.loadErrorDescription',
              'Close this window and try opening the query again.'
            )}
          </Alert>
        </Flex>
      )}
    </Modal>
  );
};
